import * as signalR from '@microsoft/signalr';
import Logger from './LoggerService';
import { HubConnectionState } from '@microsoft/signalr';

export default class SignalRConnection {
    public connection: signalR.HubConnection;
    public onClose?: (error?: Error | undefined) => void;
    private _accessToken!: string;

    constructor (readonly _tokenEndpoint: string, readonly _hubEndpoint: string) {
      if (!_tokenEndpoint) { throw new Error('Please provide Token Endpoint Url'); }
      if (!_hubEndpoint) { throw new Error('Please provide Hub Endpoint Url'); }
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this._hubEndpoint, { accessTokenFactory: () => this._accessToken })
        .build();

      this.connection.onclose(err => this.connectionClosed(err));
    }

    public on (methodName: string, onMessage: (...args: any[]) => void) {
      this.connection.on(methodName, onMessage);
    }

    public off (methodName: string) {
      this.connection.off(methodName);
    }

    public send (methodName: string, ...args: any[]): Promise<void> {
      return this.connection.send(methodName, ...args);
    }

    public invoke (methodName: string, ...args: any[]): Promise<any> {
      return this.connection.invoke(methodName, ...args);
    }

    public connect (): Promise<void> {
      if (this.connection.state === HubConnectionState.Connected) {
        return Promise.resolve();
      }
      Logger.debug('SignalRConnection: Getting token...');
      return this.getAccessToken()
        .then(token => {
          this._accessToken = token;
          Logger.debug('SignalRConnection: Starting connection...');
          return this.connection.start()
            .then(() => {
              Logger.debug('SignalRConnection: Connected succesfully');
            })
            .catch((error) => {
              Logger.error('SignalRConnection: Error', error.message);
              throw error;
            });
        })
        .catch((error) => {
          const message = 'SignalRConnection: ' + error.message;
          Logger.error(message);
          throw new Error(message);
        });
    }

    disconnect (): Promise<void> {
      if (this.connection.state === HubConnectionState.Disconnected) {
        return Promise.resolve();
      }
      return this.connection.stop();
    }

    connectionClosed (error: Error | undefined) {
      if (error && error.message) {
        console.error(error.message);
      }
      if (this.onClose) { this.onClose(error); }
    }

    getAccessToken (): Promise<string> {
      return fetch(this._tokenEndpoint, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
        .then(r => {
          if (r.status !== 200) {
            throw new Error('Failed to obtain a token. (' + r.status + ') ' + r.statusText);
          }
          return r.json();
        })
        .then(data => data.data);
    }
}
